import React from 'react';
import { formatCurrency } from 'utils';
import { useQuery } from '@apollo/client';
import { HelpOutline } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Link, Stack, Typography } from '@mui/material';

import { InventoryOptionMatch } from '../../types';
import { GET_MATCHING_INVENTORY_OPTIONS } from '../../api';

interface Props {
  year?: string | null;
  make?: string | null;
  model?: string | null;
  modelBase?: string | null;
}

interface MatchingResponse {
  matchingInventoryOptions: InventoryOptionMatch[];
}

export const EquipmentOptionsBlurb = ({ year, make, model, modelBase }: Props) => {
  const hasCriteria = Boolean(year || make || model || modelBase);

  const { data } = useQuery<MatchingResponse>(GET_MATCHING_INVENTORY_OPTIONS, {
    variables: { input: { year, make, model, modelBase } },
    skip: !hasCriteria,
    fetchPolicy: 'cache-and-network',
  });

  const options = data?.matchingInventoryOptions ?? [];

  if (options.length === 0) return null;

  return (
    <Alert severity="info" icon={<HelpOutline fontSize="inherit" />}>
      <Typography variant="body2" fontWeight={600}>
        Available Options
      </Typography>
      <Typography variant="caption" color="text.secondary">
        To edit these options please go to the individual option.
      </Typography>

      <Stack mt={1} gap={0.5}>
        {options.map(option => (
          <Stack
            key={option.inventoryOptionId}
            direction="row"
            gap={2}
            justifyContent="space-between"
            alignItems="center"
          >
            <Link
              component={RouterLink}
              to={`/inventory/options/${option.inventoryOptionId}`}
              target="_blank"
              rel="noopener"
              underline="hover"
            >
              {option.name} ({option.code})
            </Link>
            <Typography variant="body2">{formatCurrency(option.cost, true)}</Typography>
          </Stack>
        ))}
      </Stack>
    </Alert>
  );
};
